import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  ConfigManagerBranch,
  ConfigmanagerConflictResolution,
  ConfigManagerConfig,
  ConfigManagerConfigLink,
  ConfigManagerDryMergeResponse,
  ConfigManagerMergeResponse,
  ConfigManagerForkBranchRequest,
  ConfigManagerDeleteBranchRequest,
  ConfigManagerAddConfigRequest,
  ConfigManagerRenameConfigRequest,
  ConfigManagerCloneConfigRequest,
  ConfigManagerDeleteConfigRequest,
  ConfigManagerUpdateLinkRequest,
  ConfigManagerAddSectionResponse,
  ConfigManagerAddSectionRequest,
  ConfigManagerPutSectionRequest,
  ConfigManagerRenameSectionRequest,
  ConfigManagerRenameSectionBody,
  ConfigManagerDeleteSectionRequest,
  ConfigLegacy,
  ConfigManagerSectionLink,
  ConfigManagerCheckRun,
} from './api-v2.model';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { Author } from '@app/shared/author/author';
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Project, ProjectService } from '@app/providers/projects';
import { NotificationMessageService } from '@app/shared/notification-message/notification-message.service';
import { ConfigManagerService } from './api.service';
import { ConfigManagerWhitelistService } from './whitelist.service';
import { EndpointIdentifier } from './whitelist.model';
import { OptimisticLocking } from './api.model';

@Injectable()
export class ConfigManagerV2Service {
  private project: Project;
  private readonly goOrigin = environment.gonfigManagerApi;
  protected sectionOL = new OptimisticLocking(
    ({ branch, config, section }) => `${branch}/${config}/${section}`,
  );

  constructor(
    protected http: HttpClient,
    private service: ProjectService,
    private message: NotificationMessageService,
    private cmV1Api: ConfigManagerService,
    private whitelist: ConfigManagerWhitelistService,
  ) {
    this.service.project$.subscribe((project: Project) => {
      this.project = project;
    });
  }

  protected getPath(): string {
    return `${this.goOrigin}/products/${this.project.short_name}`;
  }

  // ─── Branch operations ───────────────────────────────────────────────────

  getBranches(): Observable<ConfigManagerBranch[]> {
    return this.http
      .get<ConfigManagerBranch[]>(`${this.getPath()}/branches`)
      .pipe(
        map((response) =>
          response.map((branch) =>
            Object.assign(branch, { owner: Author.clean(branch.owner) }),
          ),
        ),
        catchError(this.handleError('getBranches', [])),
      );
  }

  getBranch(branch: string): Observable<{} | ConfigManagerBranch> {
    return this.checkWhitelist(
      'getBranch',
      () =>
        this.http
          .get<ConfigManagerBranch>(`${this.getPath()}/branches/${branch}`)
          .pipe(
            map(
              (branchEntity): ConfigManagerBranch =>
                ConfigManagerBranch.create(branchEntity),
            ),
            catchError(this.handleError('getBranch')),
          ),
      () => this.cmV1Api.getBranch(branch),
    );
  }

  forkBranch(body: ConfigManagerForkBranchRequest): Observable<{}> {
    return this.checkWhitelist(
      'forkBranch',
      () =>
        this.http
          .post<{}>(`${this.getPath()}/branches`, body)
          .pipe(catchError(this.handleError<{}>('forkBranch'))),
      () =>
        this.cmV1Api.forkBranch({
          source: body.source,
          name: body.name,
          links: false,
          description: body.description,
        }),
    );
  }

  deleteBranch({ branch }: ConfigManagerDeleteBranchRequest): Observable<{}> {
    return this.checkWhitelist(
      'deleteBranch',
      () =>
        this.http
          .delete<{}>(`${this.getPath()}/branches/${branch}`)
          .pipe(catchError(this.handleError<{}>('deleteBranch'))),
      () => this.cmV1Api.deleteBranch({ branch }),
    );
  }

  merge(
    local: string,
    remote: string,
    resolutions: ConfigmanagerConflictResolution[],
  ): Observable<ConfigManagerMergeResponse> {
    return this.http.patch<ConfigManagerMergeResponse>(
      `${this.getPath()}/branches/${local}/merge/${remote}`,
      { resolutions },
    );
  }

  cloneConfig({
    branch,
    name,
    description,
    data,
    metadata,
    source,
  }: ConfigManagerCloneConfigRequest): Observable<{}> {
    return this.checkWhitelist(
      'cloneConfig',
      () =>
        this.http
          .post(`${this.getPath()}/branches/${branch}/files`, {
            branch,
            name,
            description,
            data,
            metadata,
            source,
          })
          .pipe(catchError(this.handleError('cloneConfig'))),
      () =>
        this.cmV1Api.cloneConfig({
          local: branch,
          name,
          description,
          source,
        }),
    );
  }

  dryMerge(
    local: string,
    remote: string,
    resolutions: ConfigmanagerConflictResolution[],
  ): Observable<ConfigManagerDryMergeResponse> {
    return this.http.patch<ConfigManagerDryMergeResponse>(
      `${this.getPath()}/branches/${local}/dry-merge/${remote}`,
      { resolutions },
    );
  }

  // ─── Config operations ───────────────────────────────────────────────────

  getConfigs(branch: string): Observable<ConfigManagerConfig[]> {
    return this.checkWhitelist(
      'getConfigs',
      () =>
        this.http
          .get<ConfigManagerConfig[]>(
            `${this.getPath()}/branches/${branch}/files`,
          )
          .pipe(
            map((response) => response ?? []),
            catchError(this.handleError('getConfigs', [])),
          ),
      () => this.cmV1Api.getConfigs(branch),
    );
  }

  addConfig({
    branch,
    name,
    description,
    data,
    metadata,
  }: ConfigManagerAddConfigRequest): Observable<ConfigManagerConfig[]> {
    return this.checkWhitelist(
      'addConfig',
      () =>
        this.http
          .post<ConfigManagerConfig[]>(
            `${this.getPath()}/branches/${branch}/files`,
            {
              branch,
              name,
              description,
              data,
              metadata,
            },
          )
          .pipe(catchError(this.handleError('addConfig', []))),
      () =>
        this.cmV1Api.addConfig({
          branch,
          name,
          description,
          data,
          metadata,
        }),
    );
  }

  renameConfig({
    branch,
    description,
    name,
    file,
  }: ConfigManagerRenameConfigRequest): Observable<{}> {
    return this.checkWhitelist(
      'renameConfig',
      () =>
        this.http
          .patch(`${this.getPath()}/branches/${branch}/files/${file}`, {
            description,
            name,
          })
          .pipe(catchError(this.handleError('renameConfig'))),
      () =>
        this.cmV1Api.renameConfig({
          branch,
          file,
          name,
          description,
        }),
    );
  }

  deleteConfig({
    branch,
    file,
  }: ConfigManagerDeleteConfigRequest): Observable<{}> {
    return this.checkWhitelist(
      'deleteConfig',
      () =>
        this.http
          .delete(`${this.getPath()}/branches/${branch}/files/${file}`)
          .pipe(catchError(this.handleError('deleteConfig'))),
      () => this.cmV1Api.deleteConfig({ branch, file }),
    );
  }

  // ─── Section operations ──────────────────────────────────────────────────

  getSections(branch: string, config: string): Observable<string[]> {
    return this.checkWhitelist(
      'getSections',
      () =>
        this.http
          .get<string[]>(
            `${this.getPath()}/branches/${branch}/files/${config}/sections`,
          )
          .pipe(
            map((response) => response ?? []),
            catchError(this.handleError('getSections', [])),
          ),
      () => this.cmV1Api.getSections(branch, config),
    );
  }

  getSection(
    branch: string,
    config: string,
    section: string,
  ): Observable<ConfigLegacy> {
    return this.checkWhitelist(
      'getSection',
      () =>
        this.http
          .get<ConfigLegacy>(
            `${this.getPath()}/branches/${branch}/files/${config}/sections/${section}`,
            { observe: 'response' },
          )
          .pipe(
            catchError(this.handleError('getSection')),
            map((response: HttpResponse<ConfigLegacy>) => {
              this.sectionOL.setFromHeaders(
                { branch, config, section },
                response.headers,
              );
              return response.body;
            }),
          ),
      () => this.cmV1Api.getSection(branch, config, section),
    );
  }

  addSection({
    local,
    file,
    section_name,
    data = [],
    metadata,
  }: ConfigManagerAddSectionRequest): Observable<ConfigManagerAddSectionResponse> {
    return this.checkWhitelist(
      'addSection',
      () =>
        this.http
          .post<{ position: number }>(
            `${this.getPath()}/branches/${local}/files/${file}/sections`,
            {
              local,
              file,
              section_name,
              data,
              metadata,
              project: this.project.short_name_cm,
            },
          )
          .pipe(catchError(this.handleError('addSection', {}))),
      () =>
        this.cmV1Api.addSection({
          local,
          file,
          section_name,
          data,
          metadata,
        }),
    );
  }

  putSection({
    branch,
    config,
    section,
    body,
  }: ConfigManagerPutSectionRequest): Observable<ConfigLegacy> {
    const headers = this.sectionOL.getCheckHeaders({ branch, config, section });

    return this.checkWhitelist(
      'putSection',
      () =>
        this.http
          .put<ConfigLegacy>(
            `${this.getPath()}/branches/${branch}/files/${config}/sections/${section}`,
            body,
            { headers, observe: 'response' },
          )
          .pipe(
            catchError((res: HttpErrorResponse) => {
              // Status: HTTP_BAD_REQUEST (400) or HTTP_PRECONDITION_FAILED (412) or HTTP_LOCKED (423)
              if (
                res.status === 400 ||
                res.status === 412 ||
                res.status === 423
              ) {
                return throwError(res);
              }
              return this.handleError('putSection')(res);
            }),
            map((response: HttpResponse<ConfigLegacy>) => {
              this.sectionOL.setFromHeaders(
                { branch, config, section },
                response.headers,
              );
              return response.body;
            }),
          ),
      () =>
        this.cmV1Api.putSection({
          branch,
          config,
          section,
          body: { data: body.data, metadata: body.metadata },
        }),
    );
  }

  renameSection({
    local,
    file,
    section,
    section_name,
  }: ConfigManagerRenameSectionRequest): Observable<{}> {
    const body: ConfigManagerRenameSectionBody = {
      action: 'rename',
      section_name,
    };
    return this.checkWhitelist(
      'renameSection',
      () =>
        this.http
          .put<{}>(
            `${this.getPath()}/branches/${local}/files/${file}/sections/${section}`,
            body,
          )
          .pipe(catchError(this.handleError('renameSection'))),
      () =>
        this.cmV1Api.renameSection({
          local,
          file,
          section,
          section_name,
          action: 'rename',
        }),
    );
  }

  deleteSection({
    branch,
    config,
    section,
  }: ConfigManagerDeleteSectionRequest): Observable<unknown> {
    return this.checkWhitelist(
      'deleteSection',
      () =>
        this.http
          .delete(
            `${this.getPath()}/branches/${branch}/files/${config}/sections/${section}`,
          )
          .pipe(catchError(this.handleError('deleteSection'))),
      () =>
        this.cmV1Api
          .deleteSection({ branch, config, section })
          .pipe(map(() => undefined as void)),
    );
  }

  // ─── Link operations (Google Drive) ─────────────────────────────────────

  getBranchLinks(branch: string): Observable<ConfigManagerConfigLink[]> {
    return this.checkWhitelist(
      'getBranchLinks',
      () =>
        this.http
          .get<ConfigManagerConfigLink[]>(
            `${this.getPath()}/branches/${branch}/link`,
          )
          .pipe(catchError(this.handleError('getBranchLinks', []))),
      () => this.cmV1Api.getBranchLinks(branch),
    );
  }

  getSectionLinks(
    branch: string,
    config: string,
  ): Observable<ConfigManagerSectionLink[]> {
    return this.checkWhitelist(
      'getSectionLinks',
      () =>
        this.http
          .get<ConfigManagerSectionLink[]>(
            `${this.getPath()}/branches/${branch}/files/${config}/link`,
          )
          .pipe(catchError(this.handleError('getSectionLinks', []))),
      () => this.cmV1Api.getLinks(branch, config),
    );
  }

  updateLink(
    branch: string,
    config: string,
    link: ConfigManagerUpdateLinkRequest,
  ): Observable<{}> {
    // TODO: remove this hack once demo (twodots copy) testing is done
    if (
      !this.project.config?.googleLinkEnabled &&
      this.project.alias !== 'demo'
    ) {
      return of({});
    }

    return this.checkWhitelist(
      'updateLink',
      () =>
        this.http
          .post(
            `${this.getPath()}/branches/${branch}/files/${config}/sections/${
              link.name
            }/google/link`,
            link,
          )
          .pipe(catchError(this.handleError('updateLink'))),
      () =>
        this.cmV1Api.updateLink(branch, config, {
          name: link.name,
          id: link.id,
          gid: link.gid,
        }),
    );
  }

  deleteLink(branch: string, config: string, section: string): Observable<{}> {
    // TODO: remove this hack once demo (twodots copy) testing is done
    if (
      !this.project.config?.googleLinkEnabled &&
      this.project.alias !== 'demo'
    ) {
      return of({});
    }

    return this.checkWhitelist(
      'deleteLink',
      () =>
        this.http
          .delete(
            `${this.getPath()}/branches/${branch}/files/${config}/sections/${section}/google/link`,
          )
          .pipe(catchError(this.handleError<{}>('deleteLink'))),
      () => this.cmV1Api.deleteLink(branch, config, section),
    );
  }

  // ─── Check Runs ──────────────────────────────────────────────────────────────

  getCheckRuns(branch: string): Observable<ConfigManagerCheckRun[]> {
    return this.http.get<ConfigManagerCheckRun[]>(
      `${this.getPath()}/branches/${branch}/check-runs`,
    );
  }

  // ─── Whitelist helper ────────────────────────────────────────────────────

  protected checkWhitelist<T>(
    endpoint: EndpointIdentifier,
    cmApiV2: () => Observable<T>,
    cmApiV1: () => Observable<T>,
  ): Observable<T> {
    try {
      const { allowed } = this.whitelist.isWhitelisted(endpoint);
      return allowed ? cmApiV2() : cmApiV1();
    } catch (error) {
      return new Observable((observer) => {
        observer.error(error);
      });
    }
  }

  private handleError<T>(_operation = 'operation', result?: T) {
    return ({ error }: { error: any }): Observable<T> => {
      console.error(error);
      this.message.error(error, 'Config Manager API Failed: ' + _operation);
      return of(result as T);
    };
  }
}
